import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "space-y-6"
}
const _hoisted_2 = { class: "antd-tabs" }
const _hoisted_3 = ["aria-label"]
const _hoisted_4 = ["id", "aria-selected", "aria-controls", "onClick"]
const _hoisted_5 = ["id", "aria-labelledby"]
const _hoisted_6 = {
  key: 0,
  class: "card"
}
const _hoisted_7 = { class: "mb-5 text-lg font-semibold text-gray-800" }
const _hoisted_8 = { class: "grid grid-cols-1 gap-5 text-sm md:grid-cols-2" }
const _hoisted_9 = {
  key: 4,
  class: "max-h-96 overflow-auto rounded-lg bg-gray-50 p-4 text-xs"
}
const _hoisted_10 = {
  key: 0,
  class: "flex flex-wrap gap-3 border-t border-gray-200 pt-4"
}
const _hoisted_11 = {
  key: 1,
  class: "text-gray-500"
}

import {
  computed,
  defineComponent,
  h,
  PropType,
  ref,
  watch,
} from "vue";
import Button from "@/components/common/Button.vue";
import DocumentConstructorPayloadView from "@/components/documents/documentConstructor/DocumentConstructorPayloadView.vue";
import DocumentTemplatePayloadView from "@/components/entityApproval/DocumentTemplatePayloadView.vue";
import UserPayloadView from "@/components/entityApproval/UserPayloadView.vue";
import CompanyPayloadView from "@/components/entityApproval/CompanyPayloadView.vue";
import { isDocumentConstructorPayload } from "@/utils/documentConstructorPayload";
import {
  EApprovalStatus,
  EEntityType,
  IEntityApprovalRequest,
} from "@/types/common/entityApproval/EntityApproval";
import { t } from "@/i18n";
import {
  getEntityApprovalActionLabel,
  getEntityApprovalStatusLabel,
  getEntityApprovalTypeLabel,
} from "@/utils/labels";

type ApprovalDetailSection =
  | "request"
  | "overview"
  | "parameters"
  | "processes"
  | "access"
  | "integration"
  | "template"
  | "payload";

type ConstructorSection =
  | "overview"
  | "parameters"
  | "processes"
  | "access"
  | "integration";
type TemplateSection = "overview" | "template" | "access";
type UserSection = "overview" | "access";

interface ApprovalDetailTab {
  key: ApprovalDetailSection;
  label: string;
}

interface ApprovalPayloadData extends Record<string, unknown> {
  viewScope?: unknown;
  editScope?: unknown;
  process?: unknown[];
  esedoConfig?: { enabled?: boolean };
  params?: { esedoConfig?: { enabled?: boolean } };
}


export default /*#__PURE__*/_defineComponent({
  props: {
  request: {
    type: Object as () => IEntityApprovalRequest | null,
    default: null,
  },
},
  setup(__props, { emit }: { emit: ({
  approve: [id: string];
  reject: [id: string];
}), expose: any, slots: any, attrs: any }) {

const props = __props





const activeTab = ref<ApprovalDetailSection>("request");
const payloadData = computed<ApprovalPayloadData>(
  () => (props.request?.payload as ApprovalPayloadData) || {}
);
const tabIdPrefix = computed(() => `approval-${props.request?.id || "details"}`);

const showConstructorPayload = computed(() => {
  if (!props.request?.payload) {
    return false;
  }
  return (
    props.request.entityType === EEntityType.DOCUMENT_CONSTRUCTOR &&
    isDocumentConstructorPayload(
      props.request.payload as Record<string, unknown>
    )
  );
});

const showTemplatePayload = computed(() => {
  return (
    !!props.request?.payload &&
    props.request.entityType === EEntityType.DOCUMENT_TEMPLATE
  );
});

const showUserPayload = computed(() => {
  return (
    !!props.request?.payload && props.request.entityType === EEntityType.USER
  );
});

const showCompanyPayload = computed(() => {
  return (
    !!props.request?.payload && props.request.entityType === EEntityType.COMPANY
  );
});

const hasScope = computed(() => {
  return (
    payloadData.value.viewScope != null || payloadData.value.editScope != null
  );
});

const hasEsedo = computed(() => {
  return Boolean(
    payloadData.value.esedoConfig?.enabled ||
      payloadData.value.params?.esedoConfig?.enabled
  );
});

const processCount = computed(() => {
  return Array.isArray(payloadData.value.process)
    ? payloadData.value.process.length
    : 0;
});

const tabs = computed<ApprovalDetailTab[]>(() => {
  const result: ApprovalDetailTab[] = [
    { key: "request", label: t("entityApproval.requestInfo") },
  ];

  if (showConstructorPayload.value) {
    result.push(
      {
        key: "overview",
        label: t("entityApproval.constructor.mainInfo"),
      },
      { key: "parameters", label: t("docConstructorForm.params") },
      {
        key: "processes",
        label: `${t("docConstructorForm.process")} (${processCount.value})`,
      }
    );
    if (hasScope.value) {
      result.push({ key: "access", label: t("entityScope.title") });
    }
    if (hasEsedo.value) {
      result.push({ key: "integration", label: t("esedo.title") });
    }
    return result;
  }

  if (showTemplatePayload.value) {
    result.push(
      { key: "overview", label: t("entityApproval.template.mainInfo") },
      { key: "template", label: t("documentTemplate.template") }
    );
    if (hasScope.value) {
      result.push({ key: "access", label: t("entityScope.title") });
    }
    return result;
  }

  if (showUserPayload.value) {
    result.push({
      key: "overview",
      label: t("entityApproval.user.mainInfo"),
    });
    if (hasScope.value) {
      result.push({ key: "access", label: t("entityScope.title") });
    }
    return result;
  }

  if (showCompanyPayload.value) {
    result.push({
      key: "overview",
      label: t("entityApproval.company.mainInfo"),
    });
    return result;
  }

  result.push({ key: "payload", label: t("entityApproval.payload") });
  return result;
});

const constructorSection = computed<ConstructorSection>(() => {
  const allowed: ConstructorSection[] = [
    "overview",
    "parameters",
    "processes",
    "access",
    "integration",
  ];
  return allowed.includes(activeTab.value as ConstructorSection)
    ? (activeTab.value as ConstructorSection)
    : "overview";
});

const templateSection = computed<TemplateSection>(() => {
  const allowed: TemplateSection[] = ["overview", "template", "access"];
  return allowed.includes(activeTab.value as TemplateSection)
    ? (activeTab.value as TemplateSection)
    : "overview";
});

const userSection = computed<UserSection>(() => {
  return activeTab.value === "access" ? "access" : "overview";
});

const coordinatorLabel = computed(() => {
  if (!props.request) {
    return "-";
  }
  if (props.request.coordinatorId == null) {
    return t("entityScope.coordinatorAny");
  }
  if (
    props.request.coordinatorName &&
    props.request.coordinatorName !== String(props.request.coordinatorId)
  ) {
    return props.request.coordinatorName;
  }
  return "-";
});

const formattedPayload = computed(() => {
  if (!props.request?.payload) {
    return "-";
  }
  try {
    return JSON.stringify(props.request.payload, null, 2);
  } catch {
    return String(props.request.payload);
  }
});

watch(
  tabs,
  (availableTabs) => {
    if (!availableTabs.some((tab) => tab.key === activeTab.value)) {
      activeTab.value = availableTabs[0]?.key || "request";
    }
  },
  { immediate: true }
);

watch(
  () => props.request?.id,
  () => {
    activeTab.value = "request";
  }
);

const InfoItem = defineComponent({
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number] as PropType<string | number>,
      default: "-",
    },
  },
  setup(infoProps) {
    return () =>
      h("div", [
        h(
          "span",
          { class: "text-xs font-medium uppercase tracking-wide text-gray-500" },
          infoProps.label
        ),
        h(
          "p",
          { class: "mt-1 break-words text-gray-800" },
          String(infoProps.value || "-")
        ),
      ]);
  },
});

return (_ctx: any,_cache: any) => {
  return (__props.request)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("nav", {
            class: "antd-tabs-nav flex-wrap gap-x-8 gap-y-3",
            role: "tablist",
            "aria-label": _unref(t)('entityApproval.detailsTitle')
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(tabs), (tab) => {
              return (_openBlock(), _createElementBlock("button", {
                id: `${_unref(tabIdPrefix)}-tab-${tab.key}`,
                key: tab.key,
                type: "button",
                role: "tab",
                class: _normalizeClass(["antd-tab", { 'antd-tab--active': activeTab.value === tab.key }]),
                "aria-selected": activeTab.value === tab.key,
                "aria-controls": `${_unref(tabIdPrefix)}-panel-${tab.key}`,
                onClick: ($event: any) => (activeTab.value = tab.key)
              }, _toDisplayString(tab.label), 11, _hoisted_4))
            }), 128))
          ], 8, _hoisted_3)
        ]),
        _createElementVNode("div", {
          id: `${_unref(tabIdPrefix)}-panel-${activeTab.value}`,
          role: "tabpanel",
          "aria-labelledby": `${_unref(tabIdPrefix)}-tab-${activeTab.value}`
        }, [
          (activeTab.value === 'request')
            ? (_openBlock(), _createElementBlock("section", _hoisted_6, [
                _createElementVNode("h2", _hoisted_7, _toDisplayString(_unref(t)("entityApproval.requestInfo")), 1),
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(_unref(InfoItem), {
                    label: _unref(t)('entityApproval.entityType'),
                    value: _unref(getEntityApprovalTypeLabel)(__props.request.entityType)
                  }, null, 8, ["label", "value"]),
                  _createVNode(_unref(InfoItem), {
                    label: _unref(t)('entityApproval.actionType'),
                    value: _unref(getEntityApprovalActionLabel)(__props.request.actionType)
                  }, null, 8, ["label", "value"]),
                  _createVNode(_unref(InfoItem), {
                    label: _unref(t)('entityApproval.approvalStatus'),
                    value: _unref(getEntityApprovalStatusLabel)(__props.request.status)
                  }, null, 8, ["label", "value"]),
                  _createVNode(_unref(InfoItem), {
                    label: _unref(t)('entityApproval.requester'),
                    value: __props.request.requesterName || '-'
                  }, null, 8, ["label", "value"]),
                  _createVNode(_unref(InfoItem), {
                    label: _unref(t)('entityApproval.coordinatorId'),
                    value: _unref(coordinatorLabel)
                  }, null, 8, ["label", "value"]),
                  _createVNode(_unref(InfoItem), {
                    label: _unref(t)('entityScope.directorate'),
                    value: __props.request.directorateName || '-'
                  }, null, 8, ["label", "value"])
                ])
              ]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (_unref(showConstructorPayload))
                  ? (_openBlock(), _createBlock(DocumentConstructorPayloadView, {
                      key: 0,
                      payload: __props.request.payload,
                      section: _unref(constructorSection),
                      "collapsible-processes": activeTab.value === 'processes'
                    }, null, 8, ["payload", "section", "collapsible-processes"]))
                  : (_unref(showTemplatePayload))
                    ? (_openBlock(), _createBlock(DocumentTemplatePayloadView, {
                        key: 1,
                        payload: __props.request.payload,
                        section: _unref(templateSection)
                      }, null, 8, ["payload", "section"]))
                    : (_unref(showUserPayload))
                      ? (_openBlock(), _createBlock(UserPayloadView, {
                          key: 2,
                          payload: __props.request.payload,
                          "previous-payload": __props.request.previousPayload,
                          section: _unref(userSection)
                        }, null, 8, ["payload", "previous-payload", "section"]))
                      : (_unref(showCompanyPayload))
                        ? (_openBlock(), _createBlock(CompanyPayloadView, {
                            key: 3,
                            payload: __props.request.payload
                          }, null, 8, ["payload"]))
                        : (_openBlock(), _createElementBlock("pre", _hoisted_9, _toDisplayString(_unref(formattedPayload)), 1))
              ], 64))
        ], 8, _hoisted_5),
        (__props.request.status === _unref(EApprovalStatus).WAITING)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createVNode(Button, {
                type: "button",
                class: "bg-green-600",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('approve', __props.request.id)))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)("entityApproval.approve")), 1)
                ]),
                _: 1
              }),
              _createVNode(Button, {
                type: "button",
                class: "bg-red-600",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('reject', __props.request.id)))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)("entityApproval.reject")), 1)
                ]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true)
      ]))
    : (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(_unref(t)("common.noData")), 1))
}
}

})